import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { Menu, X } from "lucide-react";
import { siteFlags } from "@/config/siteFlags";
import { scrollToBook } from "@/config/siteLinks";
const links = [
  { label: "How it works", href: "#how-it-works", testid: "nav-link-how" },
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Pricing", href: "#pricing", testid: "nav-link-pricing" },
  ...(siteFlags.showTestimonials
    ? [{ label: "Testimonials", href: "#testimonials", testid: "nav-link-testimonials" }]
    : []),
  { label: "FAQ", href: "#faq", testid: "nav-link-faq" },
  { label: "Contact", href: "/contact", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F9F9F7]/85 backdrop-blur-xl border-b border-[#1C1C1A]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="font-serif-display text-2xl tracking-tight text-[#1C1C1A]"
        >
          Job<span className="text-[#143628] italic font-semibold">Nex</span>
          <span className="text-[#D4B886]">.</span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="text-sm text-[#4A4A48] hover:text-[#143628] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:block">
          <Button
            data-testid="nav-cta-contact"
            onClick={scrollToBook}
            className="rounded-none bg-[#143628] hover:bg-[#0D241A] text-[#F9F9F7] px-5 h-10 font-medium tracking-tight"
          >
            Book a consultation
          </Button>
        </div>
        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                data-testid="nav-mobile-trigger"
                className="p-2 rounded-none border border-[#1C1C1A]/15"
              >
                <Menu className="h-5 w-5 text-[#1C1C1A]" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#F9F9F7] border-l border-[#1C1C1A]/10 w-[82%] sm:w-[360px]">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="flex items-center justify-between mb-10">
                <span className="font-serif-display text-2xl">
                  Job<span className="text-[#143628] italic">Nex</span>
                </span>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-close"
                  className="p-2"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex flex-col gap-5">
                {links.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-${l.testid}`}
                    className="font-serif-display text-3xl text-[#1C1C1A] border-b border-[#1C1C1A]/10 pb-3"
                  >
                    {l.label}
                  </a>
                ))}
              </nav>
              <Button
                data-testid="mobile-nav-cta"
                onClick={() => {
                  setOpen(false);
                  setTimeout(scrollToBook, 300);
                }}
                className="mt-10 w-full rounded-none bg-[#143628] hover:bg-[#0D241A] text-[#F9F9F7] h-12"
              >
                Book a consultation
              </Button>            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
